/**
 * "Regional distribution" — a plant's CSP regions, grouped under their parent
 * region and collapsible (parent -> regions).
 *
 * Two tiers, kept visibly apart:
 *   Recorded      `csp_region_recorded` — a GBIF herbarium voucher exists in the
 *                 region. Solid chip.
 *   Could occur   `csp_region_possible` — inferred from the regional (Jepson)
 *                 range, no voucher yet. Outlined, dashed chip.
 *
 * The backend withholds `csp_region_possible` while Jepson data is hidden
 * (lib/jepsonVisibility.js), so the "could occur" tier simply does not arrive and
 * nothing here needs its own flag check.
 */
import React, { useState } from "react";
import { Tag, Tooltip } from "antd";
import { CaretDownOutlined, CaretRightOutlined } from "@ant-design/icons";
import { CSP_REGION_PARENTS } from "utils/cspRegionParents";

export const CSP_REGION_COLUMN_KEY = "csp_region_distributions";
export const POSSIBLE_TOOLTIP = "Inferred from regional range; no herbarium record here yet.";
const RECORDED_LABEL = "Recorded";
const RECORDED_TOOLTIP = "Herbarium record (GBIF specimen) in this region.";
export const POSSIBLE_LABEL = "Could occur — not yet recorded";
const OTHER_PARENT = "Other";

interface RegionGroup {
  parent: string;
  recorded: string[];
  possible: string[];
}

/**
 * Group regions under their parents. Parents holding a recorded region come first,
 * in the order the recorded regions arrive (ranked by specimen count); parents that
 * are "could occur" only follow, alphabetically.
 */
export function groupCspRegions(
  recorded: string[] = [],
  possible: string[] = [],
  parents: string[] = []
): RegionGroup[] {
  const groups = new Map<string, RegionGroup>();
  const groupFor = (region: string) => {
    const parent = CSP_REGION_PARENTS[region] || OTHER_PARENT;
    if (!groups.has(parent)) groups.set(parent, { parent, recorded: [], possible: [] });
    return groups.get(parent)!;
  };
  recorded.forEach((r) => groupFor(r).recorded.push(r));
  const recordedParents = Array.from(groups.keys());
  possible.filter((r) => !recorded.includes(r)).forEach((r) => groupFor(r).possible.push(r));
  const possibleOnly = Array.from(groups.keys())
    .filter((p) => !recordedParents.includes(p))
    .sort((a, b) => {
      // Follow the server's parent order where it has one; unknown parents last.
      const ia = parents.indexOf(a), ib = parents.indexOf(b);
      if (ia !== ib && ia >= 0 && ib >= 0) return ia - ib;
      if (a === OTHER_PARENT || b === OTHER_PARENT) return a === OTHER_PARENT ? 1 : -1;
      return a.localeCompare(b);
    });
  return [...recordedParents, ...possibleOnly].map((p) => groups.get(p)!);
}

// "Sierra Nevada (High) — high peaks & subalpine" under its own parent reads
// better as just the tier; keep the full name in the tooltip.
function shortName(region: string, parent: string): string {
  const [head, tier] = region.split(" — ");
  return tier && parent !== OTHER_PARENT ? `${head === parent ? "" : `${head} — `}${tier}` : region;
}

const recordedTagStyle: React.CSSProperties = { marginBottom: 4 };
const possibleTagStyle: React.CSSProperties = {
  marginBottom: 4,
  background: "transparent",
  borderStyle: "dashed",
};

function ParentGroup({ group, defaultOpen }: { group: RegionGroup; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const counts = [
    group.recorded.length ? `${group.recorded.length} recorded` : null,
    group.possible.length ? `${group.possible.length} could occur` : null,
  ].filter(Boolean).join(", ");
  return (
    <div className="csp-region-group" style={{ textAlign: "left" }}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        aria-expanded={open}
        style={{ background: "none", border: 0, padding: 0, cursor: "pointer", textAlign: "left" }}
      >
        {open ? <CaretDownOutlined /> : <CaretRightOutlined />}{" "}
        <strong>{group.parent}</strong>{" "}
        <span style={{ opacity: 0.65 }}>({counts})</span>
      </button>
      {open && (
        <div style={{ paddingLeft: 16, paddingTop: 4 }}>
          {group.recorded.map((r) => (
            <Tooltip key={r} title={<><div>{r}</div><div><strong>{RECORDED_LABEL}.</strong> {RECORDED_TOOLTIP}</div></>}>
              <Tag color="green" style={recordedTagStyle} data-tier="recorded">{shortName(r, group.parent)}</Tag>
            </Tooltip>
          ))}
          {group.possible.map((r) => (
            <Tooltip key={r} title={<><div>{r}</div><div><strong>{POSSIBLE_LABEL}.</strong> {POSSIBLE_TOOLTIP}</div></>}>
              <Tag bordered style={possibleTagStyle} data-tier="possible">{shortName(r, group.parent)}</Tag>
            </Tooltip>
          ))}
        </div>
      )}
    </div>
  );
}

interface CellProps {
  recorded?: string[] | null;
  possible?: string[] | null;
  parents?: string[] | null;
}

export function CspRegionCell({ recorded, possible, parents }: CellProps) {
  const groups = groupCspRegions(recorded || [], possible || [], parents || []);
  if (!groups.length) return <span style={{ opacity: 0.45 }}>—</span>;
  return (
    <div>
      {groups.map((g) => (
        <ParentGroup key={g.parent} group={g} defaultOpen={groups.length === 1} />
      ))}
    </div>
  );
}

/** The table column. Rows carry the csp_region_* fields from the collection API. */
export function buildCspRegionColumn() {
  return {
    // A plain string: the column picker (SelectColumn) renders `title` as its label.
    // The two tiers are explained on each chip's tooltip instead.
    title: "Regional distribution",
    key: CSP_REGION_COLUMN_KEY,
    dataIndex: CSP_REGION_COLUMN_KEY,
    width: 320,
    show: true,
    // Most recorded regions first — a rough "how widespread" ordering.
    sorter: (a: any, b: any) =>
      ((a && a.csp_region_recorded) || []).length - ((b && b.csp_region_recorded) || []).length,
    render: (_: any, record: any) => (
      <CspRegionCell
        recorded={record && record.csp_region_recorded}
        possible={record && record.csp_region_possible}
        parents={record && record.csp_region_parents}
      />
    ),
  };
}
